import type React from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useSupplierReconciliation } from '../../../../api/useSupplierReconciliation'
import { extractAndDisplayError } from '../../../../utils/errorUtils'
import type { RecMatchRow, components } from '../../../../types/api/v1'
import { getDaysAgo } from '../Launchpad/components/utils/getDaysAgo'
import { SecurityContext } from '../../../Navigation/SecurityContext'
import { useSyncedLocalStorage } from '@hooks/settings'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

type SupplierRecSnapshot = components['schemas']['SupplierRecSnapshot']

// Shared data interface for both Launchpad and Management pages
interface SharedHomeData {
  matches: RecMatchRow[]
  isLoading: boolean
  isBackgroundRefreshing: boolean
  trends: SupplierRecSnapshot[]
  trendsLoading: boolean
  hasError: boolean
  error: {
    matchViewError?: any
    trendsError?: any
  }
}

// Context interface
interface SharedHomeContextValue {
  data: SharedHomeData
  assignedToMe: boolean
  setAssignedToMe: (value: boolean) => void
  dateRange: {
    start: Date
    end: Date
  }
}

// Create context
const SharedHomeContext = createContext<SharedHomeContextValue | undefined>(undefined)

// Fixed 30-day period for all data
const FIXED_DAYS = 30
const REFRESH_INTERVAL = 3 * 60 * 1000 // 3 minutes in milliseconds

// Provider component
export function SharedHomeProvider({ children }: { children: React.ReactNode }) {
  const { useMatchOverviewOpen, useSupplierRecDashboardTrends } = useSupplierReconciliation()
  const { user } = useContext(SecurityContext)

  // State for assigned to me toggle - persisted in localStorage and synced with server
  const [assignedToMe, setAssignedToMe] = useSyncedLocalStorage('supplier_rec_assigned_to_me', false)

  // Fixed 30-day period - memoize to prevent re-renders
  const { currentPeriodStart, currentPeriodEnd } = useMemo(() => {
    // Use dayjs for proper timezone-aware date handling
    // End of today in user's local timezone
    const endDate = dayjs().endOf('day').toDate()

    // Start of day 30 days ago in user's local timezone
    const startDate = dayjs().subtract(FIXED_DAYS, 'day').startOf('day').toDate()

    return {
      currentPeriodStart: startDate,
      currentPeriodEnd: endDate,
    }
  }, []) // Empty deps - only calculate once on mount

  // Query parameters
  const queryParams = useMemo(
    () => ({
      dateRange: {
        start: currentPeriodStart,
        end: currentPeriodEnd,
      },
      filters: {}, // We'll filter client-side for now
    }),
    [currentPeriodStart, currentPeriodEnd]
  )

  // Fetch data with 3-minute refetch interval
  const {
    data: matchViewData,
    isLoading,
    isFetching: isBackgroundRefreshing,
    error: matchViewError,
  } = useMatchOverviewOpen(queryParams, {
    refetchInterval: REFRESH_INTERVAL,
    // Don't refresh until current query completes
    refetchIntervalInBackground: false,
  })

  // Fetch trends data
  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useSupplierRecDashboardTrends(
    { today: dayjs().utc().toDate() }, // Use UTC to ensure consistent timezone handling
    {
      refetchInterval: REFRESH_INTERVAL,
      refetchIntervalInBackground: false,
    }
  )

  // Handle query errors using React Query v5 pattern
  useEffect(() => {
    if (matchViewError) {
      extractAndDisplayError(matchViewError, 'Failed to load match data')
    }
  }, [matchViewError])

  useEffect(() => {
    if (trendsError) {
      extractAndDisplayError(trendsError, 'Failed to load dashboard trends')
    }
  }, [trendsError])

  // Extract and filter matches
  const matches = useMemo(() => {
    if (!matchViewData?.rows) return []

    const allMatches = matchViewData.rows as RecMatchRow[]

    // Filter by assignee if toggle is on
    if (assignedToMe && user?.username) {
      return allMatches.filter((match) => match.assignee?.username === user.username)
    }

    return allMatches
  }, [matchViewData, assignedToMe, user])

  // Process trends data to handle assignee filtering
  const processedTrends = useMemo(() => {
    if (!trendsData || trendsData.length === 0) return []

    // If assignedToMe is true, filter metrics by current user
    if (assignedToMe && user?.username) {
      return trendsData.map((snapshot) => {
        // Find assignee-specific metrics
        const findAssigneeValue = (byAssigneeArray: any[]) => {
          const assigneeData = byAssigneeArray.find((item) => item.assignee?.username === user.username)
          return assigneeData?.value || 0
        }

        // Return filtered snapshot
        return {
          ...snapshot,
          // Use assignee-specific values if available, otherwise keep original
          total_open_matches: findAssigneeValue(snapshot.open_matches_by_assignee) || snapshot.total_open_matches,
          total_past_due: findAssigneeValue(snapshot.past_due_by_assignee) || snapshot.total_past_due,
          total_orphan_bols: findAssigneeValue(snapshot.orphan_bols_by_assignee) || snapshot.total_orphan_bols,
          // Auto match percentage stays the same (0 in this data)
          auto_match_percentage: snapshot.auto_match_percentage,
        }
      })
    }

    return trendsData
  }, [trendsData, assignedToMe, user])

  // Shared data object
  const data: SharedHomeData = useMemo(
    () => ({
      matches,
      isLoading,
      isBackgroundRefreshing,
      trends: processedTrends,
      trendsLoading,
      hasError: !!(matchViewError || trendsError),
      error: {
        matchViewError,
        trendsError,
      },
    }),
    [matches, isLoading, isBackgroundRefreshing, processedTrends, trendsLoading, matchViewError, trendsError]
  )

  const value: SharedHomeContextValue = {
    data,
    assignedToMe,
    setAssignedToMe,
    dateRange: {
      start: currentPeriodStart,
      end: currentPeriodEnd,
    },
  }

  return <SharedHomeContext.Provider value={value}>{children}</SharedHomeContext.Provider>
}

// Hook to use the context
export function useSharedHome() {
  const context = useContext(SharedHomeContext)
  if (!context) {
    throw new Error('useSharedHome must be used within a SharedHomeProvider')
  }
  return context
}
